import { useEffect, useState } from "react";
import { registerAndSendVerification, checkVerificationAndDelete } from "./auth-logic";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import bgHero from "../assets/bghero1.jpg";

const registerSchema = z.object({
  name: z.string().min(2, "Nama minimal 2 karakter"),
  email: z.string().email("Email tidak valid"),
  password: z.string().min(6, "Password minimal 6 karakter"),
});

export const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(registerSchema),
  });

  const handleRegister = async (data) => {
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const { message } = await registerAndSendVerification(data.email, data.name, data.password);
      setSuccessMessage(message);

      setTimeout(async () => {
        await checkVerificationAndDelete();
      }, 120000);

      setTimeout(() => {
        navigate("/login");
      }, 5000);
    } catch (error) {
      setErrorMessage(`Terjadi kesalahan: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center px-4"
      style={{ backgroundImage: `url(${bgHero})` }}
    >
      <div className="fixed top-0 left-0 w-full h-full bg-cyan-900 bg-opacity-40 backdrop-blur-sm"></div>
      <motion.div
        className="relative z-0 max-w-4xl w-full mx-auto bg-white bg-opacity-40 shadow-lg backdrop-blur-md grid md:grid-cols-2 rounded-xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        {/* Kolom kiri */}
        <div
          className="hidden md:flex flex-col justify-center text-white bg-cover bg-center p-8 h-full relative rounded-tl-xl rounded-bl-xl"
          style={{ backgroundImage: `url(${bgHero})` }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-cyan-900 bg-opacity-80 backdrop-blur-sm z-0 rounded-tl-xl rounded-bl-xl"></div>

          {/* Teks dengan z-index lebih tinggi */}
          <motion.h1
            className="text-4xl font-bold mb-4 drop-shadow-md z-0 relative"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Selamat Datang
          </motion.h1>
          <motion.p
            className="text-lg drop-shadow-md z-20 relative"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
          >
            Masuk untuk mengakses akun Anda dan nikmati pengalaman terbaik
            bersama kami.
          </motion.p>
        </div>

        {/* Kolom kanan */}
        <div className="flex flex-col p-6 z-10">
          <p className="text-2xl font-semibold mb-6 text-center text-white">
            Daftar
          </p>
          <form
            onSubmit={handleSubmit(handleRegister)}
            className="flex flex-col gap-4 text-black"
          >
            <label className="text-left text-white">Nama</label>
            <input
              type="text"
              placeholder="Nama Lengkap"
              {...register("name")}
              className="p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600"
            />
            {errors.name && (
              <p className="text-sm text-white -mt-2">{errors.name.message}</p>
            )}

            <label className="text-left text-white">Email</label>
            <input
              type="email"
              placeholder="Email"
              {...register("email")}
              className="p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600"
            />
            {errors.email && (
              <p className="text-sm text-white -mt-2">{errors.email.message}</p>
            )}

            <label className="text-left text-white">Password</label>
            <input
              type="password"
              placeholder="Password"
              {...register("password")}
              className="p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600"
            />
            {errors.password && (
              <p className="text-sm text-white -mt-2">{errors.password.message}</p>
            )}

            {errorMessage && (
              <p className="text-sm text-white text-center -mt-2">
                {errorMessage}
              </p>
            )}
            {successMessage && (
              <p className="text-sm text-white text-center -mt-2">
                {successMessage}
              </p>
            )}

            <button
              type="submit"
              className="mt-4 w-full bg-slate-600 text-white py-3 rounded-xl hover:bg-slate-700 disabled:opacity-50"
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading ? "Memproses..." : "Daftar"}
            </button>
          </form>

          <div className="text-center mt-4 text-white">
            <p>
              Sudah punya akun?{" "}
              <Link to="/Login" className="text-cyan-800 hover:underline">
                Login di sini
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};